import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import {
  downloadDocument,
  getTemplateDocuments,
  issueClaimSettlementDocuments
} from '../../../../../lib/requests.es6'

// The statuses claims-dcp issues settlement documents for (approved for payment).
export const SETTLEMENT_DOCUMENT_STATUSES = [
  'Approved',
  'Approved_Partially_Paid',
  'Approved_Paid',
  'Ex_Gracia_Approved'
]

// The two Claim templates, found by name: see the SAMBA claim-docs README in
// policy-document-place-holder.
export const SETTLEMENT_DOCUMENTS = [
  {
    key: 'voucher',
    label: 'Claim Settlement Voucher (Règlement)',
    match: /r[eè]glement/i,
    file: 'reglement'
  },
  {
    key: 'receipt',
    label: 'Discharge Receipt (Quittance)',
    match: /quittance/i,
    file: 'quittance'
  }
]

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  window.URL.revokeObjectURL(url)
}

/**
 * CR005: lets an agent print the settlement voucher and discharge receipt of an
 * approved claim. The first download issues the documents' numbers and freezes
 * what they print; every later download reprints the same documents. Customers
 * have no email, so downloading is the only way the documents leave the system.
 */
const SettlementDocuments = props => {
  const { claim } = props
  const [issued, setIssued] = useState(claim.settlement_document || null)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  const approved = SETTLEMENT_DOCUMENT_STATUSES.includes(claim.status)

  const download = async document => {
    setBusy(document.key)
    setError(null)
    try {
      const { data: settlement } = await issueClaimSettlementDocuments(
        claim.guid
      )
      setIssued(settlement)

      const { data: templates } = await getTemplateDocuments('Claim')
      const template = (templates || []).find(t =>
        document.match.test(t.name || '')
      )
      if (!template) {
        throw new Error(
          `No "${document.label}" template has been uploaded for this partner.`
        )
      }

      const { data: pdf } = await downloadDocument({
        template_guid: template.guid,
        entity_guid: claim.guid
      })
      saveBlob(pdf, `${claim.claim_number}-${document.file}.pdf`)
    } catch (e) {
      const message = e.response && e.response.data && e.response.data.message
      setError(
        message ||
          e.message ||
          'The document could not be downloaded, please try again.'
      )
    } finally {
      setBusy(null)
    }
  }

  if (!approved) {
    return (
      <p data-test="settlement-not-available">
        The settlement voucher and discharge receipt can be downloaded once the
        claim is approved.
      </p>
    )
  }

  return (
    <div>
      {issued && issued.data ? (
        <p data-test="settlement-issued">
          Voucher N° {issued.data.letter_number} and receipt N°{' '}
          {issued.data.receipt_number}, issued {issued.data.issue_date}
          {issued.issued_by_name ? ` by ${issued.issued_by_name}` : ''}. Every
          download reprints these same documents.
        </p>
      ) : (
        <p data-test="settlement-not-issued">
          The first download gives both documents their numbers and fixes what
          they print.
        </p>
      )}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {SETTLEMENT_DOCUMENTS.map(document => (
          <ButtonInc
            key={document.key}
            data-test={`settlement-download-${document.key}`}
            variant="contained"
            color="primary"
            size="large"
            disabled={busy !== null}
            onClick={() => download(document)}
          >
            {busy === document.key ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              document.label
            )}
          </ButtonInc>
        ))}
      </div>
      {error && (
        <p
          data-test="settlement-error"
          style={{ color: '#d32f2f', marginTop: '12px' }}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default SettlementDocuments
